/**
 * iter-68 — Single shared segmented-control component used everywhere
 * we need a "pick exactly one of N options" toggle.
 *
 * PART 1 fix (iter-68):
 *   The previous implementation used `transition-colors` and the
 *   `.tap` helper. On iOS Safari this combo could land the user in a
 *   state where the freshly-active button stayed visually inactive
 *   until a scroll or paint trigger nudged the layout. Two root
 *   causes:
 *     1. A 120ms background-color transition meant the new colour
 *        faded in just as the user lifted their finger, and the
 *        old `:active` scale on the previously-active button
 *        masked the change for a moment.
 *     2. The `.tap` class participates in the global
 *        `data-scrolling="1"` guard that suppresses :active state
 *        during flicks — but if the touch is registered AS a scroll
 *        (>6px move), the visual update is suppressed for the next
 *        ~100ms.
 *   We now rely on plain instant-paint utilities (no .tap, no
 *   transition-colors) and force the active background via a
 *   `data-active` attribute that CSS targets directly. iOS Safari
 *   paints the swap in a single frame.
 */
export default function SegmentedControl({
    value,
    onChange,
    options,
    variant = "tabs",
    ariaLabel,
    testid,
    className = "",
}) {
    const safeOptions = Array.isArray(options) ? options : [];
    if (safeOptions.length === 0) return null;

    const buttonBase =
        // Common per-button classes — instant paint, no fade, kills
        // tap-highlight, suppresses sticky-hover on touch via the
        // `[data-active]` selector in index.css.
        "segmented-button min-tap inline-flex items-center justify-center gap-2 select-none cursor-pointer";

    if (variant === "pills") {
        return (
            <div
                role="tablist"
                aria-label={ariaLabel}
                data-testid={testid}
                className={`flex gap-2 flex-wrap ${className}`}
            >
                {safeOptions.map((opt) => {
                    const active = opt.value === value;
                    return (
                        <button
                            key={opt.value}
                            type="button"
                            role="tab"
                            aria-selected={active}
                            data-active={active ? "true" : "false"}
                            data-testid={
                                opt.testid ||
                                (testid ? `${testid}-${opt.value}` : undefined)
                            }
                            onClick={() => onChange(opt.value)}
                            className={`${buttonBase} segmented-pill px-4 rounded-full text-xs font-medium border`}
                        >
                            {opt.label}
                            {opt.count != null ? (
                                <span className="segmented-count ml-1 text-[10px]">
                                    {opt.count}
                                </span>
                            ) : null}
                        </button>
                    );
                })}
            </div>
        );
    }

    // "tabs" variant — uniform grid pills inside a card frame.
    const cols = safeOptions.length;
    return (
        <div
            role="tablist"
            aria-label={ariaLabel}
            data-testid={testid}
            className={`grid gap-1 p-1 rounded-xl border border-white/10 bg-card ${className}`}
            style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}
        >
            {safeOptions.map((opt) => {
                const active = opt.value === value;
                return (
                    <button
                        key={opt.value}
                        type="button"
                        role="tab"
                        aria-selected={active}
                        data-active={active ? "true" : "false"}
                        data-testid={
                            opt.testid ||
                            (testid ? `${testid}-${opt.value}` : undefined)
                        }
                        onClick={() => onChange(opt.value)}
                        className={`${buttonBase} segmented-tab rounded-lg text-sm font-medium`}
                    >
                        {opt.icon ? <opt.icon size={14} /> : null}
                        <span>{opt.label}</span>
                        {opt.count != null ? (
                            <span className="segmented-count text-[10px]">
                                {opt.count}
                            </span>
                        ) : null}
                    </button>
                );
            })}
        </div>
    );
}
